import { addDays, addMonths, daysInMonth, monthKey, toDate, todayIso, weekday } from './FieldOpsKit'
import { DEPOT, driveMinutes, orderNearest, pathMiles, placeInCity, roadMiles, seededRandom as seeded } from './geo'

/**
 * Field Ops demo data (rewritten 2026-11-04 for the field app). NOTHING here comes from the system. Route names and
 * salesperson codes follow the real Sage structure (a route can have several salespeople); store names, addresses,
 * phones, coordinates, goals, sales, visits and deliveries are invented. The generator is seeded, so every reload
 * shows the same starting data.
 *
 * What changed vs 2026-10-29: stores have coordinates and each salesperson's day is geographically coherent (so the
 * map reads like a real route); deliveries are grouped by driver area and ordered from the warehouse; visits and
 * deliveries carry check-in/out times; and there is a new "field" slice (days, returns, chat) built by buildFieldSeed.
 */

/* ---------- demo identities (the "Demo user" selector) ---------- */
export const DEMO_USERS = [
    { id: 'pablo', name: 'Pablo', title: 'Super admin', kind: 'admin', role: 'super' },
    { id: 'paulo', name: 'Paulo', title: 'Approver', kind: 'admin', role: 'approver' },
    { id: 'josue', name: 'Josué', title: 'Editor', kind: 'admin', role: 'editor' },
    { id: 'jeymar', name: 'Jeymar', title: 'Editor', kind: 'admin', role: 'editor' },
    { id: 'randolph', name: 'Randolph', title: 'Editor', kind: 'admin', role: 'editor' },
    { id: 'rep-RT', name: 'Salesperson RT', title: 'Salesperson', kind: 'rep', rep: 'RT' },
    { id: 'rep-JR', name: 'Salesperson JR', title: 'Salesperson', kind: 'rep', rep: 'JR' },
    { id: 'rep-JC', name: 'Salesperson JC', title: 'Salesperson', kind: 'rep', rep: 'JC' },
    { id: 'drv-d1', name: 'Carlos M.', title: 'Driver', kind: 'driver', driverId: 'd1' },
    { id: 'drv-d2', name: 'Luis R.', title: 'Driver', kind: 'driver', driverId: 'd2' },
    { id: 'drv-d3', name: 'Miguel A.', title: 'Driver', kind: 'driver', driverId: 'd3' },
    { id: 'drv-d4', name: 'Jorge P.', title: 'Driver', kind: 'driver', driverId: 'd4' },
    { id: 'drv-d5', name: 'Andrés V.', title: 'Driver', kind: 'driver', driverId: 'd5' },
]
export const REPS = ['RT', 'JR', 'JC']

export const NO_ORDER_REASONS = ['Has enough stock', 'Cash is short', 'Owner not available', 'Waiting on a price', 'Other']
export const CLOSED_REASONS = ['Closed for the day', 'Owner away', 'Holiday / event', 'Out of business?', 'Other']
export const MISS_REASONS = ['Ran out of time', 'Store was closed', 'Vehicle problem', 'Traffic / weather', 'Sick / personal', 'Store asked to come later', 'Other']
export const FAIL_REASONS = ['Customer not available', 'Refused the delivery', 'Wrong address', 'Store closed', 'Other']
export const CANCEL_REASONS = ['Customer cancelled', 'Invoice voided', 'Duplicated delivery', 'Other']
export const RESCHEDULE_REASONS = ['Customer asked', 'Truck not available', 'Driver not available', 'Order not ready', 'Other']
export const RETURN_REASONS = ['Damaged', 'Expired / short date', 'Wrong item delivered', 'Overstock / did not sell', 'Quality complaint', 'Pricing error', 'Other']
export const RETURN_DISPOSITIONS = [
    { key: 'pickup', label: 'Take it back now', text: 'You load it and bring it to the warehouse.' },
    { key: 'credit', label: 'Credit only', text: 'The store keeps the product (a manager decides).' },
    { key: 'destroy', label: 'Destroy at the store', text: 'Discard it there. A photo is required.' },
]
export const CHAT_TOPICS = [
    { key: 'general', label: 'General' }, { key: 'schedule', label: 'Route / schedule' }, { key: 'complaint', label: 'Complaint' },
    { key: 'return', label: 'Return' }, { key: 'price', label: 'Price question' }, { key: 'incident', label: 'Incident' },
]
export const WINDOWS = ['8–10 AM', '9–11 AM', '10–12 PM', '11–1 PM', '12–2 PM', '1–3 PM', '2–4 PM', '3–5 PM']
export const FREQUENCIES = [{ key: 'weekly', label: 'Every week' }, { key: 'biweekly', label: 'Every 2 weeks' }, { key: 'monthly', label: 'Once a month' }]

export const PRODUCT_CATEGORIES = ['Tortillas', 'Dairy', 'Sauces', 'Dry goods', 'Beverages', 'Snacks']
export const PRODUCTS = [
    { sku: 'TOR-1201', name: 'Corn tortillas 12 ct', price: 14.5, min: 11.8, cat: 'Tortillas', stock: 420 },
    { sku: 'TOR-2402', name: 'Flour tortillas 24 ct', price: 17.2, min: 13.9, cat: 'Tortillas', stock: 310 },
    { sku: 'TOR-0603', name: 'Tostadas 60 ct', price: 21.4, min: 17.1, cat: 'Tortillas', stock: 96 },
    { sku: 'CRM-1602', name: 'Mexican crema 16 oz', price: 22.0, min: 17.5, cat: 'Dairy', stock: 180 },
    { sku: 'QSO-0803', name: 'Queso fresco 8 oz', price: 31.0, min: 25.2, cat: 'Dairy', stock: 74 },
    { sku: 'QSO-1604', name: 'Oaxaca cheese 16 oz', price: 38.5, min: 31.0, cat: 'Dairy', stock: 12 },
    { sku: 'LCH-3205', name: 'Whole milk 1 gal', price: 9.8, min: 7.9, cat: 'Dairy', stock: 260 },
    { sku: 'SLS-1204', name: 'Green salsa 12 oz', price: 18.75, min: 14.9, cat: 'Sauces', stock: 240 },
    { sku: 'SLS-1205', name: 'Red salsa 12 oz', price: 18.75, min: 14.9, cat: 'Sauces', stock: 225 },
    { sku: 'MOL-1006', name: 'Mole paste 10 oz', price: 26.3, min: 21.0, cat: 'Sauces', stock: 58 },
    { sku: 'CHL-0506', name: 'Dried chiles 5 oz', price: 12.4, min: 9.8, cat: 'Dry goods', stock: 330 },
    { sku: 'FRJ-2005', name: 'Refried beans 20 oz', price: 19.25, min: 15.4, cat: 'Dry goods', stock: 500 },
    { sku: 'ARZ-2007', name: 'Rice 2 lb', price: 16.9, min: 13.5, cat: 'Dry goods', stock: 610 },
    { sku: 'FRJ-4008', name: 'Black beans 4 lb', price: 21.9, min: 17.5, cat: 'Dry goods', stock: 0 },
    { sku: 'MSA-4409', name: 'Corn masa flour 4.4 lb', price: 15.6, min: 12.4, cat: 'Dry goods', stock: 380 },
    { sku: 'AGU-2408', name: 'Bottled water 24 pk', price: 24.0, min: 19.2, cat: 'Beverages', stock: 700 },
    { sku: 'BEB-1210', name: 'Tropical soda 12 pk', price: 29.8, min: 23.9, cat: 'Beverages', stock: 410 },
    { sku: 'BEB-0611', name: 'Horchata 1 L (6 pk)', price: 27.4, min: 21.9, cat: 'Beverages', stock: 33 },
    { sku: 'JGO-1212', name: 'Fruit juice 12 pk', price: 26.1, min: 20.9, cat: 'Beverages', stock: 190 },
    { sku: 'GAL-1009', name: 'Cookies assorted 10 ct', price: 27.6, min: 22.0, cat: 'Snacks', stock: 260 },
    { sku: 'BOT-2413', name: 'Chili lime chips 24 ct', price: 23.5, min: 18.8, cat: 'Snacks', stock: 345 },
    { sku: 'CAC-1214', name: 'Peanuts with chile 12 ct', price: 14.9, min: 11.9, cat: 'Snacks', stock: 120 },
    { sku: 'DUL-3015', name: 'Mexican candy mix 30 ct', price: 19.9, min: 15.9, cat: 'Snacks', stock: 88 },
    { sku: 'PAN-1216', name: 'Sweet bread 12 ct', price: 20.6, min: 16.5, cat: 'Snacks', stock: 64 },
]

/* ======================================================================================================
 * SALES REPS
 * ==================================================================================================== */

// [salesperson, weekday (1 = Mon), route, [[city, stores], ...]] — every day is one coherent area, so the map reads like
// a real day. Route names and the salesperson mix per route follow Sage (Albertville, Knoxville... can have several).
const DAY_PLAN = [
    ['RT', 1, 'Chattanooga', [['Chattanooga, TN', 6], ['Cleveland, TN', 3]]],
    ['RT', 2, 'Knoxville', [['Knoxville, TN', 5], ['Maryville, TN', 3]]],
    ['RT', 3, 'Rome/Cedartown', [['Rome, GA', 4], ['Cedartown, GA', 2], ['Cartersville, GA', 2]]],
    ['RT', 4, 'Albertville', [['Albertville, AL', 4], ['Boaz, AL', 2], ['Guntersville, AL', 2]]],
    ['RT', 5, 'Knoxville', [['Sevierville, TN', 3], ['Knoxville, TN', 3]]],
    ['JR', 1, 'Birmingham', [['Birmingham, AL', 5], ['Hoover, AL', 2], ['Bessemer, AL', 1]]],
    ['JR', 2, 'Tuscaloosa', [['Tuscaloosa, AL', 5], ['Northport, AL', 2]]],
    ['JR', 3, 'Mobile/Montgomery', [['Montgomery, AL', 3], ['Prattville, AL', 2]]],
    ['JR', 3, 'Columbus/Opelika', [['Auburn, AL', 2], ['Opelika, AL', 2]]],
    ['JR', 4, 'Albertville', [['Albertville, AL', 3], ['Guntersville, AL', 2], ['Boaz, AL', 1]]],
    ['JR', 5, 'Florence', [['Florence, AL', 4], ['Muscle Shoals, AL', 2]]],
    ['JC', 1, 'Atlanta', [['Atlanta, GA', 4], ['Marietta, GA', 2], ['Norcross, GA', 2]]],
    ['JC', 2, 'Gainsville/Buford', [['Gainesville, GA', 5], ['Buford, GA', 3]]],
    ['JC', 3, 'Valdosta', [['Valdosta, GA', 4], ['Tifton, GA', 2], ['Moultrie, GA', 2]]],
    ['JC', 4, 'Talpa', [['Kennesaw, GA', 3], ['Marietta, GA', 2]]],
    ['JC', 4, 'Dalton', [['Calhoun, GA', 2]]],
    ['JC', 5, 'Dalton', [['Dalton, GA', 4], ['Calhoun, GA', 2]]],
]

const NAME_A = ['El Sol', 'La Esperanza', 'Los Amigos', 'Mi Pueblo', 'La Hacienda', 'El Ranchero', 'San José', 'La Michoacana', 'El Progreso', 'Las Palmas', 'Don Chuy', 'La Plaza', 'El Tesoro', 'Santa Rosa', 'Mi Tierra', 'La Canasta', 'El Zócalo', 'Los Primos', 'La Fiesta', 'El Paso', 'La Unión', 'Costa Rica', 'Oaxaca', 'Yucatán', 'Guadalajara', 'La Poblana', 'El Hermano', 'La Raza', 'Cuscatlán', 'La Tapatía',
    'La Bonita', 'El Rincón', 'Las Delicias', 'Don Pepe', 'La Favorita', 'El Quetzal', 'Los Arcos', 'Casa Linda', 'El Mexicano', 'La Costeña', 'San Juan', 'La Villa', 'El Dorado', 'Tierra Caliente', 'Los Reyes', 'La Nueva', 'El Faro', 'Plaza Azteca', 'Mi Familia', 'El Patrón', 'La Mazorca', 'El Molino', 'Las Flores', 'Santa María', 'Buenavista', 'La Perla', 'El Bosque', 'Los Pinos', 'Doña Rosa', 'El Camino']
const NAME_B = ['Market', 'Supermercado', 'Tienda', 'Mercado', 'Food Mart', 'Abarrotes']
const STREETS = ['Main St', 'Broad St', 'Oak Ave', 'Industrial Blvd', 'Highway 11', 'Pine St', 'Market St', 'Church St', 'Dixie Hwy', 'Lakeview Dr']
const CONTACTS = ['Mrs. Lopez', 'Mr. Ramírez', 'Carmen', 'Don Pedro', 'Luisa', 'Mr. Herrera', 'Rosa', 'Mr. Castillo', 'Elena', 'Javier', 'Mrs. Morales', 'Tomás']
const HOURS = ['7 AM – 9 PM', '8 AM – 8 PM', '9 AM – 7 PM', '8 AM – 6 PM']

const round250 = (n) => Math.round(n / 250) * 250
const pad2 = (n) => (n < 10 ? '0' + n : '' + n)
const hmm = (min) => Math.floor(min / 60) + ':' + pad2(Math.round(min) % 60)
const atMinutes = (iso, min) => toDate(iso).getTime() + Math.round(min) * 60000

/** Planned visit dates for one store between two ISO dates (inclusive), following its day + frequency. */
export const plannedDates = (store, fromIso, toIso) => {
    const out = []
    let d = fromIso
    const epoch = toDate('2024-01-01').getTime() // a Monday
    while (d <= toIso) {
        if (weekday(d) === store.day) {
            const dom = toDate(d).getDate()
            if (store.freq === 'weekly') out.push(d)
            else if (store.freq === 'biweekly') {
                const weekIdx = Math.floor((toDate(d).getTime() - epoch) / (7 * 86400000))
                if (weekIdx % 2 === store.parity) out.push(d)
            } else if (Math.ceil(dom / 7) === store.monthlyWeek) out.push(d)
        }
        d = addDays(d, 1)
    }
    return out
}

export const buildSalesSeed = () => {
    const rnd = seeded(20261104)
    const today = todayIso()
    const thisMonth = monthKey(today)
    const months = [-2, -1, 0, 1].map((n) => addMonths(thisMonth, n))

    // stores: one block per (salesperson, weekday); inside a block they are ordered nearest-neighbour
    const stores = []
    const usedNames = {}
    let n = 0
    DAY_PLAN.forEach((plan) => {
        const block = []
        plan[3].forEach((pair) => {
            for (let i = 0; i < pair[1]; i++) {
                let name = ''
                do { name = NAME_B[Math.floor(rnd() * NAME_B.length)] + ' ' + NAME_A[Math.floor(rnd() * NAME_A.length)] } while (usedNames[name])
                usedNames[name] = true
                n++
                const place = placeInCity(pair[0], rnd)
                const hold = n % 31 === 0
                block.push({
                    id: 's' + n, name: name, route: plan[2], rep: plan[0], city: pair[0],
                    address: (100 + Math.floor(rnd() * 8900)) + ' ' + STREETS[Math.floor(rnd() * STREETS.length)],
                    phone: '(555) 010-' + String(1000 + Math.floor(rnd() * 8999)),
                    lat: place.lat, lng: place.lng,
                    contact: CONTACTS[Math.floor(rnd() * CONTACTS.length)], hours: HOURS[Math.floor(rnd() * HOURS.length)],
                    hold: hold, balance: hold ? 1800 + Math.floor(rnd() * 2400) : (n % 9 === 0 ? 300 + Math.floor(rnd() * 900) : 0),
                    day: plan[1], freq: n % 7 === 0 ? 'biweekly' : n % 11 === 0 ? 'monthly' : 'weekly',
                    seq: 0, parity: n % 2, monthlyWeek: 1 + (n % 4),
                })
            }
        })
        const ordered = orderNearest(block, block[0])
        // leave a small inefficiency in some days so "Optimize order" has something to find
        if (ordered.length >= 5 && rnd() < 0.7) { const t = ordered[1]; ordered[1] = ordered[3]; ordered[3] = t }
        ordered.forEach((s, i) => { s.seq = i + 1 })
        ordered.forEach((s) => stores.push(s))
    })
    // blocks that share the same (rep, day) — e.g. JR Wed has two routes — continue the sequence
    const seen = {}
    stores.forEach((s) => { const k = s.rep + s.day; seen[k] = (seen[k] || 0) + 1; s.seq = seen[k] })

    // goals: three published months + next month still being prepared
    const goals = {}
    months.forEach((m) => { goals[m] = {} })
    stores.forEach((s) => {
        const base = round250(2500 + rnd() * 6500)
        goals[months[0]][s.id] = round250(base * 0.95)
        goals[months[1]][s.id] = round250(base * 1.0)
        goals[months[2]][s.id] = round250(base * 1.04)
        if (rnd() < 0.7) goals[months[3]][s.id] = round250(base * 1.08)
    })
    const now = Date.now()
    const periods = {}
    months.forEach((m, i) => {
        periods[m] = i < 3
            ? { status: 'published', submittedBy: 'Josué', submittedAt: now - (40 - i * 12) * 86400000, approvedBy: 'Paulo', approvedAt: now - (39 - i * 12) * 86400000 }
            : { status: 'draft', submittedBy: null, submittedAt: null, approvedBy: null, approvedAt: null }
    })

    // visits: every planned occurrence of every store, past ones resolved
    const visits = []
    let vId = 0
    months.forEach((m, mi) => {
        const from = m + '-01'
        const monthEnd = m + '-' + String(daysInMonth(m)).padStart(2, '0')
        const to = mi === 3 ? addDays(today, 14) : monthEnd
        if (to < from) return
        stores.forEach((s) => {
            plannedDates(s, from, to < monthEnd ? to : monthEnd).forEach((d) => {
                vId++
                const v = { id: 'v' + vId, storeId: s.id, rep: s.rep, date: d, status: 'planned', outcome: null, reason: '', notes: '', time: '', soNo: '', extra: false, ord: s.seq, checkInAt: null, checkOutAt: null, dur: 0, distMi: null, far: false, missReason: '', photos: 0, retIds: [] }
                if (d < today) {
                    if (rnd() < 0.82) {
                        v.status = 'done'
                        const r = rnd()
                        v.outcome = r < 0.6 ? 'order' : r < 0.92 ? 'no_order' : 'closed'
                        if (v.outcome === 'no_order') v.reason = NO_ORDER_REASONS[Math.floor(rnd() * NO_ORDER_REASONS.length)]
                        if (v.outcome === 'closed') v.reason = CLOSED_REASONS[Math.floor(rnd() * CLOSED_REASONS.length)]
                    } else {
                        v.status = 'missed'
                        v.missReason = MISS_REASONS[Math.floor(rnd() * MISS_REASONS.length)]
                    }
                }
                visits.push(v)
            })
        })
    })

    // clock times for the resolved visits: a believable day (start ~8:10, drive legs, 8-40 minutes in each store)
    const storeById = {}
    stores.forEach((s) => { storeById[s.id] = s })
    const byDay = {}
    visits.forEach((v) => { if (v.status === 'done' || v.status === 'missed') { const k = v.rep + '|' + v.date; (byDay[k] = byDay[k] || []).push(v) } })
    Object.keys(byDay).forEach((k) => {
        const list = byDay[k].sort((a, b) => a.ord - b.ord)
        let t = 8 * 60 + 5 + Math.floor(rnd() * 25)
        let prev = null
        list.forEach((v) => {
            if (v.status !== 'done') return
            const st = storeById[v.storeId]
            if (prev) t += driveMinutes(roadMiles(prev, st))
            const dur = v.outcome === 'closed' ? 2 + Math.floor(rnd() * 4) : v.outcome === 'order' ? 18 + Math.floor(rnd() * 24) : 8 + Math.floor(rnd() * 14)
            v.time = hmm(t)
            v.checkInAt = atMinutes(v.date, t)
            t += dur
            v.checkOutAt = atMinutes(v.date, t)
            v.dur = dur
            v.distMi = rnd() < 0.9 ? Math.round(rnd() * 12) / 100 : Math.round((0.4 + rnd() * 1.8) * 10) / 10
            v.far = v.distMi > 0.25
            prev = st
        })
    })

    // orders: placed on the "order" visits so sales follow the visits; totals aimed at a share of the goal
    const orders = []
    let oId = 0
    const daysThisMonth = daysInMonth(thisMonth)
    const elapsed = toDate(today).getDate() / daysThisMonth
    stores.forEach((s) => {
        months.slice(0, 3).forEach((m, mi) => {
            const goal = goals[m][s.id]
            const ratio = 0.5 + rnd() * 0.95
            const target = mi === 2 ? goal * ratio * elapsed : goal * ratio
            let dates = visits.filter((v) => v.storeId === s.id && v.outcome === 'order' && monthKey(v.date) === m)
            if (dates.length === 0) {
                const dayNo = mi === 2 ? 1 + Math.floor(rnd() * Math.max(1, toDate(today).getDate() - 1)) : 3 + Math.floor(rnd() * 20)
                const d = m + '-' + String(Math.max(1, Math.min(daysInMonth(m), dayNo))).padStart(2, '0')
                if (d < today) dates = [{ date: d }]
            }
            if (dates.length === 0) return
            const weights = dates.map(() => 0.5 + rnd())
            const sum = weights.reduce((a, b) => a + b, 0)
            dates.forEach((dv, i) => {
                const amount = target * weights[i] / sum
                const picks = []
                const count = 2 + Math.floor(rnd() * 3)
                for (let k = 0; k < count; k++) picks.push(PRODUCTS[Math.floor(rnd() * PRODUCTS.length)])
                const lines = []
                picks.forEach((p) => {
                    if (lines.some((l) => l.sku === p.sku)) return
                    const qty = Math.max(1, Math.round((amount / count) / p.price))
                    lines.push({ sku: p.sku, name: p.name, qty: qty, price: p.price })
                })
                oId++
                const total = lines.reduce((a, l) => a + l.qty * l.price, 0)
                const order = { id: 'o' + oId, soNo: 'AS00' + (100 + oId), storeId: s.id, rep: s.rep, date: dv.date, lines: lines, total: Math.round(total * 100) / 100, demo: false, visitId: dv.id || null, notes: '', pending: false }
                orders.push(order)
                if (dv.id) dv.soNo = order.soNo
            })
        })
    })

    const goalHistory = [
        { id: 'h1', month: thisMonth, storeId: stores[2].id, kind: 'goal', from: round250(goals[thisMonth][stores[2].id] * 0.9), to: goals[thisMonth][stores[2].id], by: 'Josué', at: now - 31 * 86400000, note: 'Adjusted after last quarter' },
        { id: 'h2', month: thisMonth, storeId: null, kind: 'status', from: 'draft', to: 'pending', by: 'Josué', at: now - 29 * 86400000, note: '' },
        { id: 'h3', month: thisMonth, storeId: null, kind: 'status', from: 'pending', to: 'published', by: 'Paulo', at: now - 28 * 86400000, note: '' },
    ]
    const notes = {}
    notes[stores[0].id] = [{ id: 'n1', text: 'Owner prefers morning visits. Ask for Mrs. Lopez.', by: 'Salesperson ' + stores[0].rep, at: now - 12 * 86400000 }]
    notes[stores[1].id] = [{ id: 'n2', text: 'Back door delivery only — the front is blocked by the display.', by: 'Salesperson ' + stores[1].rep, at: now - 20 * 86400000 }]

    return { stores: stores, goals: goals, periods: periods, visits: visits, orders: orders, goalHistory: goalHistory, notes: notes, counters: { v: vId, o: oId, h: 3, n: 2 } }
}

/* ======================================================================================================
 * DRIVERS
 * ==================================================================================================== */

// Each driver covers an area; each day goes to one cluster of it (so the stops of a day are close to each other).
const DRIVER_AREAS = {
    d1: [['Atlanta, GA', 'Marietta, GA', 'Norcross, GA'], ['Gainesville, GA', 'Buford, GA'], ['Kennesaw, GA', 'Marietta, GA', 'Cartersville, GA']],
    d2: [['Birmingham, AL', 'Hoover, AL', 'Bessemer, AL'], ['Tuscaloosa, AL', 'Northport, AL'], ['Montgomery, AL', 'Prattville, AL']],
    d3: [['Chattanooga, TN', 'Cleveland, TN', 'Dalton, GA'], ['Rome, GA', 'Cedartown, GA', 'Cartersville, GA'], ['Calhoun, GA', 'Dalton, GA']],
    d4: [['Knoxville, TN', 'Maryville, TN', 'Sevierville, TN']],
    d5: [['Albertville, AL', 'Boaz, AL', 'Guntersville, AL'], ['Florence, AL', 'Muscle Shoals, AL']],
}

export const buildDriversSeed = (stores) => {
    const rnd = seeded(77124)
    const today = todayIso()
    const vehicles = [
        { id: 'v1', name: 'Truck 12', type: '26 ft box', capacityLb: 9000, active: true },
        { id: 'v2', name: 'Truck 08', type: '24 ft box', capacityLb: 8000, active: true },
        { id: 'v3', name: 'Van 03', type: 'Cargo van', capacityLb: 3000, active: true },
        { id: 'v4', name: 'Truck 15', type: '26 ft box', capacityLb: 9000, active: true },
        { id: 'v5', name: 'Van 05', type: 'Cargo van', capacityLb: 3000, active: true },
        { id: 'v6', name: 'Truck 02', type: '16 ft box', capacityLb: 5000, active: false },
    ]
    const drivers = [
        { id: 'd1', name: 'Carlos M.', phone: '(555) 010-2201', vehicleId: 'v1', active: true },
        { id: 'd2', name: 'Luis R.', phone: '(555) 010-2202', vehicleId: 'v2', active: true },
        { id: 'd3', name: 'Miguel A.', phone: '(555) 010-2203', vehicleId: 'v3', active: true },
        { id: 'd4', name: 'Jorge P.', phone: '(555) 010-2204', vehicleId: 'v4', active: true },
        { id: 'd5', name: 'Andrés V.', phone: '(555) 010-2205', vehicleId: 'v5', active: true },
    ]
    const now = Date.now()
    const deliveries = []
    let invNo = 380
    let dId = 0
    const shuffle = (arr) => { const a = arr.slice(); for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(rnd() * (i + 1)); const t = a[i]; a[i] = a[j]; a[j] = t } return a }
    const startHour = (w) => Number((w.match(/\d+/) || ['8'])[0])

    /** One driver's day: pick stores of one cluster, order them from the warehouse, give them growing time windows. */
    const makeGroup = (offset, driverId, statuses) => {
        const clusters = DRIVER_AREAS[driverId || 'd1']
        const cl = clusters[(Math.abs(offset) + Number((driverId || 'd1').slice(1))) % clusters.length]
        const pool = shuffle(stores.filter((s) => cl.indexOf(s.city) >= 0)).slice(0, statuses.length)
        const picked = orderNearest(pool, DEPOT)
        const date = addDays(today, offset)
        const seqs = []
        picked.forEach((st, i) => {
            const spec = statuses[i]
            const status = spec[0]
            const extra = spec[1]
            dId++; invNo += 1 + Math.floor(rnd() * 3)
            seqs.push(dId)
            const win = WINDOWS[Math.min(WINDOWS.length - 1, Math.floor((i * WINDOWS.length) / picked.length))]
            const lineCount = 2 + Math.floor(rnd() * 4)
            const lines = []
            shuffle(PRODUCTS).slice(0, lineCount).forEach((p) => { lines.push({ sku: p.sku, name: p.name, qty: 2 + Math.floor(rnd() * 14), price: p.price }) })
            const amount = Math.round(lines.reduce((a, l) => a + l.qty * l.price, 0) * 100) / 100
            const cod = rnd() < 0.22
            const d = {
                id: 'dl' + dId, invoiceNo: 'AI00' + invNo, customer: st.name, storeId: st.id, address: st.address, city: st.city, phone: st.phone,
                lat: st.lat, lng: st.lng, contact: st.contact, window: win, weightLb: Math.round((150 + rnd() * 1250) / 10) * 10,
                lines: lines, amount: amount, terms: cod ? 'COD' : 'Net 7',
                notes: rnd() < 0.2 ? 'Call 15 minutes before arriving.' : (st.id === 's2' ? 'Back door only.' : ''), driverId: driverId, date: date, originalDate: date,
                status: status, deliveredOn: null, seq: dId, events: [{ ts: now + (offset - 1) * 86400000, type: 'created', by: 'Dispatcher', note: '' }],
                arrivedAt: null, doneAt: null, receivedBy: '', signed: false, distMi: null, payment: null, retIds: [], shortLines: [],
            }
            const dr = driverId ? drivers.filter((x) => x.id === driverId)[0] : null
            if (driverId) d.events.push({ ts: now + (offset - 1) * 86400000 + 600000, type: 'assigned', by: 'Dispatcher', note: 'Assigned to ' + dr.name })
            if (status === 'delivered') {
                const late = extra === 'late'
                d.deliveredOn = late ? addDays(date, 1) : date
                const at = startHour(win) * 60 + 20 + Math.floor(rnd() * 70)
                d.arrivedAt = atMinutes(d.deliveredOn, at)
                d.doneAt = atMinutes(d.deliveredOn, at + 8 + Math.floor(rnd() * 14))
                d.receivedBy = st.contact
                d.signed = true
                d.distMi = Math.round(rnd() * 12) / 100
                if (cod) d.payment = { amount: amount, method: rnd() < 0.6 ? 'Check' : 'Cash' }
                d.events.push({ ts: d.doneAt, type: 'delivered', by: dr.name, note: '' })
            }
            if (status === 'out') d.events.push({ ts: now - 3600000, type: 'out', by: dr.name, note: '' })
            if (extra === 'failedBefore') d.events.splice(2, 0, { ts: now + (offset - 2) * 86400000, type: 'failed', by: dr.name, note: 'Customer not available' })
            deliveries.push(d)
        })
        return picked.length
    }

    // past days: resolved
    for (let off = -6; off <= -1; off++) {
        drivers.forEach((dr) => {
            if (rnd() < 0.2) return
            const count = 3 + Math.floor(rnd() * 4)
            const specs = []
            for (let i = 0; i < count; i++) {
                const r = rnd()
                specs.push(r < 0.74 ? ['delivered'] : r < 0.88 ? ['delivered', 'late'] : r < 0.94 ? ['delivered', 'failedBefore'] : r < 0.97 ? ['cancelled'] : ['planned'])
            }
            makeGroup(off, dr.id, specs)
        })
    }
    // today: in progress
    makeGroup(0, 'd1', [['planned'], ['planned'], ['planned'], ['planned'], ['planned'], ['planned'], ['planned']])
    makeGroup(0, 'd2', [['delivered'], ['delivered'], ['out'], ['planned'], ['planned'], ['planned']])
    makeGroup(0, 'd3', [['planned'], ['planned'], ['planned'], ['planned'], ['planned']])
    makeGroup(0, 'd4', [['planned'], ['planned'], ['planned'], ['planned']])
    makeGroup(0, 'd5', [['planned'], ['planned'], ['planned']])
    makeGroup(1, null, [['planned'], ['planned']])
    // next days: planned
    ;[1, 2, 3].forEach((off) => {
        drivers.forEach((dr) => { makeGroup(off, dr.id, Array.from({ length: 3 + Math.floor(rnd() * 3) }, () => ['planned'])) })
    })

    const ready = []
    for (let i = 0; i < 9; i++) {
        const st = stores[Math.floor(rnd() * stores.length)]
        invNo += 1 + Math.floor(rnd() * 3)
        ready.push({ invoiceNo: 'AI00' + invNo, customer: st.name, address: st.address, city: st.city, phone: st.phone, weightLb: Math.round((300 + rnd() * 3000) / 10) * 10, amount: Math.round(900 + rnd() * 5200), invoiceDate: i < 5 ? today : addDays(today, -1) })
    }
    return { vehicles: vehicles, drivers: drivers, deliveries: deliveries, ready: ready, counters: { d: dId, drv: 5, veh: 6 } }
}

/* ======================================================================================================
 * FIELD (days, returns, chat) — what the salesperson / driver does in the field app
 * ==================================================================================================== */

export const buildFieldSeed = (sales, drivers) => {
    const rnd = seeded(990031)
    const today = todayIso()
    const now = Date.now()
    const days = {}
    const storeById = {}
    sales.stores.forEach((s) => { storeById[s.id] = s })

    // salesperson days (past): from the resolved visits
    const repGroups = {}
    sales.visits.forEach((v) => { if (v.date < today && (v.status === 'done' || v.status === 'missed')) { const k = 'rep-' + v.rep + '|' + v.date; (repGroups[k] = repGroups[k] || []).push(v) } })
    const odo = {}
    Object.keys(repGroups).sort((a, b) => (a.split('|')[1] < b.split('|')[1] ? -1 : 1)).forEach((k) => {
        const actor = k.split('|')[0]
        const list = repGroups[k].sort((a, b) => a.ord - b.ord)
        const done = list.filter((v) => v.status === 'done')
        if (done.length === 0) return
        if (!odo[actor]) odo[actor] = 52000 + Math.floor(rnd() * 30000)
        const mi = Math.round(pathMiles(done.map((v) => storeById[v.storeId])) + 18 + rnd() * 30)
        const lastDay = k.split('|')[1] === addDays(today, -1)
        days[k] = { startedAt: done[0].checkInAt - 12 * 60000, endedAt: done[done.length - 1].checkOutAt + 14 * 60000, startOdo: odo[actor], endOdo: odo[actor] + mi, miles: mi, submitted: lastDay ? rnd() < 0.5 : rnd() < 0.94, submittedAt: done[done.length - 1].checkOutAt + 20 * 60000, note: '' }
        odo[actor] += mi
    })

    // driver days (past): from the resolved deliveries
    const drvGroups = {}
    drivers.deliveries.forEach((d) => { if (d.driverId && d.date < today && d.status === 'delivered' && d.doneAt && d.deliveredOn < today) { const k = 'drv-' + d.driverId + '|' + d.deliveredOn; (drvGroups[k] = drvGroups[k] || []).push(d) } })
    Object.keys(drvGroups).sort((a, b) => (a.split('|')[1] < b.split('|')[1] ? -1 : 1)).forEach((k) => {
        const actor = k.split('|')[0]
        const list = drvGroups[k].sort((a, b) => a.doneAt - b.doneAt)
        if (!odo[actor]) odo[actor] = 120000 + Math.floor(rnd() * 40000)
        const pts = list.map((d) => ({ lat: d.lat, lng: d.lng }))
        const mi = Math.round(pathMiles(pts, DEPOT) + roadMiles(pts[pts.length - 1], DEPOT))
        days[k] = { startedAt: list[0].arrivedAt - 45 * 60000, endedAt: list[list.length - 1].doneAt + 50 * 60000, startOdo: odo[actor], endOdo: odo[actor] + mi, miles: mi, submitted: rnd() < 0.95, submittedAt: list[list.length - 1].doneAt + 55 * 60000, note: '' }
        odo[actor] += mi
    })

    // drivers that are already out today (they have finished stops): their day is open
    const todayDone = drivers.deliveries.filter((d) => d.driverId && d.date === today && d.arrivedAt)
    Array.from(new Set(todayDone.map((d) => d.driverId))).forEach((id) => {
        const mine = todayDone.filter((d) => d.driverId === id).sort((a, b) => a.arrivedAt - b.arrivedAt)
        const all = drivers.deliveries.filter((d) => d.driverId === id && d.date === today)
        days['drv-' + id + '|' + today] = { startedAt: mine[0].arrivedAt - 40 * 60000, startOdo: 131000 + Math.floor(rnd() * 9000), loadChecked: all.length, vehicleOk: true, vehicleNote: '', submitted: false }
    })

    // returns
    const doneVisits = sales.visits.filter((v) => v.status === 'done' && v.outcome === 'order' && v.date < today)
    const retLine = (p, qty, reason) => ({ sku: p.sku, name: p.name, qty: qty, reason: reason, price: p.price })
    const mkRet = (n, src, ref, storeId, actor, by, ageDays, lines, disp, status, note) => {
        const at = now - ageDays * 86400000
        const total = Math.round(lines.reduce((a, l) => a + l.qty * l.price, 0) * 100) / 100
        const hist = [{ ts: at, type: 'requested', by: by, note: '' }]
        if (status !== 'requested') hist.push({ ts: at + 5400000, type: status === 'rejected' ? 'rejected' : 'approved', by: 'Paulo', note: status === 'rejected' ? 'Product was past the return window.' : '' })
        if (status === 'credited') hist.push({ ts: at + 86400000, type: 'credited', by: 'Accounting', note: 'Credit memo created' })
        return { id: 'r' + n, no: 'RT-' + String(100 + n), source: src, refId: ref, storeId: storeId, actor: actor, by: by, at: at, date: new Date(at).toISOString().slice(0, 10), lines: lines, disposition: disp, soRef: '', notes: note, photos: disp === 'destroy' ? 1 : (n % 2), total: total, status: status, history: hist }
    }
    const P = (i) => PRODUCTS[i]
    const sv = (i) => doneVisits[i * 7 % Math.max(1, doneVisits.length)]
    const returns = []
    if (doneVisits.length > 0) {
        returns.push(mkRet(1, 'visit', sv(1).id, sv(1).storeId, 'rep-' + sv(1).rep, 'Salesperson ' + sv(1).rep, 1, [retLine(P(3), 6, 'Expired / short date')], 'pickup', 'requested', 'Boxes were at the back of the cooler.'))
        returns.push(mkRet(2, 'visit', sv(2).id, sv(2).storeId, 'rep-' + sv(2).rep, 'Salesperson ' + sv(2).rep, 3, [retLine(P(0), 10, 'Damaged'), retLine(P(8), 4, 'Damaged')], 'destroy', 'approved', 'Pallet got wet in the truck.'))
        returns.push(mkRet(3, 'visit', sv(3).id, sv(3).storeId, 'rep-' + sv(3).rep, 'Salesperson ' + sv(3).rep, 6, [retLine(P(19), 5, 'Overstock / did not sell')], 'credit', 'rejected', ''))
        returns.push(mkRet(4, 'visit', sv(4).id, sv(4).storeId, 'rep-' + sv(4).rep, 'Salesperson ' + sv(4).rep, 9, [retLine(P(15), 3, 'Quality complaint')], 'pickup', 'credited', 'Customer says the taste is off.'))
    }
    const dDone = drivers.deliveries.filter((d) => d.status === 'delivered' && d.driverId && d.date < today)
    if (dDone.length > 2) {
        const d1 = dDone[1]; const d2 = dDone[Math.floor(dDone.length / 2)]
        returns.push(mkRet(5, 'delivery', d1.id, d1.storeId, 'drv-' + d1.driverId, drivers.drivers.filter((x) => x.id === d1.driverId)[0].name, 2, [retLine(P(6), 8, 'Wrong item delivered')], 'pickup', 'requested', 'Ordered 2% milk, we delivered whole.'))
        returns.push(mkRet(6, 'delivery', d2.id, d2.storeId, 'drv-' + d2.driverId, drivers.drivers.filter((x) => x.id === d2.driverId)[0].name, 5, [retLine(P(9), 2, 'Damaged')], 'pickup', 'approved', ''))
    }

    // chat: one conversation per salesperson / driver with the office
    const threads = {}
    const meta = {}
    let mId = 0
    const msg = (actor, from, by, text, ageMin, topic, extra) => {
        mId++
        const list = threads[actor] = threads[actor] || []
        list.push(Object.assign({ id: 'm' + mId, from: from, by: by, text: text, at: now - ageMin * 60000, topic: topic || 'general', ref: null, photo: false, pending: false, status: 'read' }, extra || {}))
    }
    msg('rep-RT', 'manager', 'Paulo', 'Remember the Albertville stores want the new price list this week.', 2900, 'general')
    msg('rep-RT', 'worker', 'Salesperson RT', 'Got it. Mrs. Lopez asked for a better price on the crema, can we do 20.50?', 2870, 'price')
    msg('rep-RT', 'manager', 'Paulo', 'Yes, 20.50 for orders of 10+ cases. Note it on the order.', 2850, 'price')
    msg('rep-RT', 'manager', 'Josué', 'Good morning! Please check the display at the first store on today’s route.', 35, 'general', { status: 'delivered' })
    msg('rep-JR', 'worker', 'Salesperson JR', 'The Hoover store closed down for good. Should I remove it from my agenda?', 4300, 'schedule')
    msg('rep-JR', 'manager', 'Randolph', 'Thanks. I’ll mark it inactive and let you know.', 4250, 'schedule')
    msg('rep-JR', 'manager', 'Randolph', 'Done — it’s off your agenda starting tomorrow.', 4100, 'schedule')
    msg('rep-JC', 'manager', 'Paulo', 'Promotion on tortillas starts Monday, make sure you offer it.', 5000, 'general')
    msg('rep-JC', 'worker', 'Salesperson JC', 'Understood. One customer is complaining about a late delivery last week.', 4980, 'complaint')
    msg('drv-d1', 'manager', 'Dispatcher', 'Carlos, the Atlanta stops are ready to load at 7:30.', 1500, 'general')
    msg('drv-d1', 'worker', 'Carlos M.', 'On it. Truck 12 has a tire warning light, I’ll check it before leaving.', 1480, 'incident')
    msg('drv-d2', 'worker', 'Luis R.', 'Customer refused 6 cases of juice, wrong flavor. Photo attached.', 1900, 'return', { photo: true })
    msg('drv-d2', 'manager', 'Dispatcher', 'Bring it back to the warehouse, we’ll open a return.', 1860, 'return')
    msg('drv-d2', 'worker', 'Luis R.', 'Back at the warehouse, unloading the 6 cases now.', 20, 'return', { status: 'delivered' })
    msg('drv-d3', 'manager', 'Dispatcher', 'Miguel, tomorrow you take the Rome route, 6 stops.', 900, 'schedule', { status: 'delivered' })
    Object.keys(threads).forEach((k) => {
        const list = threads[k]
        const lastW = list.filter((m) => m.from === 'worker').slice(-1)[0]
        const lastM = list.filter((m) => m.from === 'manager').slice(-1)[0]
        meta[k] = { resolved: false, readByWorker: list.filter((m) => m.status === 'read').slice(-1)[0] ? list.filter((m) => m.status === 'read').slice(-1)[0].at : 0, readByManager: lastW ? lastW.at : 0 }
        if (lastM && lastM.status === 'delivered') meta[k].readByWorker = lastM.at - 1
    })
    // two conversations with something waiting for the office, so the inbox shows unread items from the start
    ;['rep-JC', 'drv-d2'].forEach((k) => {
        const lastW = threads[k].filter((m) => m.from === 'worker').slice(-1)[0]
        if (lastW) meta[k].readByManager = lastW.at - 1
    })

    return { online: true, gps: 'sim', away: false, days: days, returns: returns, threads: threads, meta: meta, positions: {}, counters: { r: returns.length, m: mId } }
}
